import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { MSelect } from "../Shared"
import { Button } from "react-md";
import responseHandler from "responseHandler";
import { CalendarUpdateEnum, InspectorServiceRegionAddRequest, InspectorServiceRegionResponse, InspectorServiceTravelRuleAddRequest, InspectorServiceTravelRuleResponse, InspectorServiceTravelRuleUpdateRequest, ServiceRegionSlim } from "interfaces/Mueller.Business.Models";
import MSectionCollection, { MSECTION_MODE_TAB } from "components/Shared/MSectionCollection";
import RegionManagementPane from "./RegionManagementPane";
import { isSameRegion, ServiceRegionChange } from ".";
import TravelRuleCard from "./TravelRuleCard";
import { TravelRuleEntryForm } from "mueller-controls";
import { addInspectorTravelRule, updateInspectorTravelRule, deleteInspectorTravelRule } from "requests/inspectorServiceTravelRule";
import moment, { Moment } from "moment";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import LoadingCover from "components/Shared/LoadingCover";
import { addInspectorServiceRegion, deleteInspectorServiceRegion } from "requests/inspectorServiceRegions";
import IconButton from "components/Shared/IconButton";
import { MSelectOption } from "components/Shared/MSelect";
import { getStateList } from "actions/appraiser";

const newRuleId = -1;

interface Props {
  inspectorNumber: number,
  // has to be a ref to the function because the button wont re render every time this is updated, causing stale referenced functions to be called
  onDeleteRef?: React.MutableRefObject<(row: InspectorServiceRegionResponse) => void>,
  selectedServiceRegions?: InspectorServiceRegionResponse[],
  inspectorServiceRegionUpdates?: ServiceRegionChange[],
  travelRules?: InspectorServiceTravelRuleResponse[],
  isLoading?: boolean,
  isSavingRegions?: boolean,
  isEditingMSAs?: boolean,
  availableMSAList?: ServiceRegionSlim[],
  onMSAEditsSaved?: (oldSelected: number[], newSelected: number[]) => void,
  reloadTravelRulesAndRegions?: () => void,
  setEditingTravelRule?: (travelRule: InspectorServiceTravelRuleResponse, clearOutServiceRegionUpdates: boolean) => void,
  copyTravelRuleRegions?: (travelRule: InspectorServiceTravelRuleResponse, newTravelRuleId: number) => void,
  travelFilter: MSelectOption,
  setTravelFilter: (value: MSelectOption) => void,
  onTravelRuleHover: (rule: InspectorServiceTravelRuleResponse) => void,
  onTabChanged: (tabId: RegionManagementTabId) => void,
  stateList: { id: number, stateAbbreviation: string, stateName: string }[],
  getStateList: () => void,
  errorToast: (response: Response) => void,
};

export type RegionManagementTabId = "primaryTab" | "secondaryTab";

export const travelRuleFilterOptions = [
  { label: "Display all events", value: null },
  { label: "Display only active events", value: moment().add(1, "day").startOf("day").toString() },
];

const RegionManagementTabs = ({
  inspectorNumber,
  onDeleteRef,
  selectedServiceRegions,
  inspectorServiceRegionUpdates,
  travelRules,
  isLoading,
  isSavingRegions,
  availableMSAList,
  onMSAEditsSaved,
  reloadTravelRulesAndRegions,
  setEditingTravelRule,
  copyTravelRuleRegions,
  travelFilter,
  setTravelFilter,
  onTravelRuleHover,
  onTabChanged,
  stateList,
  getStateList,
  errorToast,
}: Props) => {
  const mounted = useRef(true);
  const dialogContext = useContext<DialogProviderValue>(DialogContext);

  const [currentTabId, setCurrentTabId] = useState<RegionManagementTabId>("primaryTab");

  useEffect(() => onTabChanged(currentTabId), [currentTabId]);

  const [isEditingMSAs, setIsEditingMSAs] = useState(false);
  const [msaEditorSelectedIndicies, setMSAEditorSelectedIndicies] = useState<number[]>();

  const [editingRule, _setEditingRule] = useState<InspectorServiceTravelRuleResponse>();
  const setEditingRule = (rule: InspectorServiceTravelRuleResponse, clearOutServiceRegionUpdates: boolean = true) => {
    _setEditingRule(rule);
    setEditingTravelRule(rule, clearOutServiceRegionUpdates);
  };

  const [editingRuleExtraData, setEditingRuleExtraData] = useState<{ dateToEdit: Date, editType: CalendarUpdateEnum }>();
  const [isSavingRule, setIsSavingRule] = useState<boolean>(false);

  const tomorrow = moment().add(1, "day").startOf("day");

  useEffect(() => {
    if (!stateList?.length) getStateList();

    return () => { mounted.current = false; };
  }, []);
  
  const getSelectedMSAIndicies = () =>
    availableMSAList
    .map((d, i) => selectedServiceRegions.find(r => isSameRegion(r, { serviceRegionId: d.id } as InspectorServiceRegionResponse)) ? i : null)
    .filter(i => i !== null);

  const changeIsEditingMSAs = (isEditing: boolean) => {
    if (isEditing) {
      setMSAEditorSelectedIndicies(getSelectedMSAIndicies);
    }

    setIsEditingMSAs(isEditing);
  };

  const blockActionIfPrimaryChangesPresent = (callbackIfSure: () => void) => {
    if (inspectorServiceRegionUpdates?.length) {
      let s = inspectorServiceRegionUpdates.length === 1 ? "" : "s";
      dialogContext.showYesNo(
        "Unsaved Changes",
        `You have ${inspectorServiceRegionUpdates.length} unsaved change${s} to primary coverage, would you like to discard those changes to continue?`,
        callbackIfSure
      );
    } else {
      callbackIfSure();
    }
  };

  const saveEditedMSAList = () => {
    let oldSelected = getSelectedMSAIndicies();
    let newSelected = msaEditorSelectedIndicies;

    onMSAEditsSaved(oldSelected, newSelected);

    changeIsEditingMSAs(false);
  };

  const onDeleteRule = (travelRule: InspectorServiceTravelRuleResponse, areYouSure?: boolean) => {
    if (areYouSure === true) {
      setIsSavingRule(true);
      deleteInspectorTravelRule({ updateType: CalendarUpdateEnum.EntireEvent, travelRuleId: travelRule.id, dateToUpdate: null }, () => {
        setIsSavingRule(false);
        reloadTravelRulesAndRegions();  
      }, mounted);
    } else {
      dialogContext.showYesNo("Delete Secondary Event", `Are you sure you want to delete the secondary event: ${travelRule.name}?`, () => onDeleteRule(travelRule, true));
    }
  };

  const createNewRule = (ruleToCopy?: InspectorServiceTravelRuleResponse) => {
    let newRule = {
      id: newRuleId,
      inspectorNumber: inspectorNumber,
      startingLocationType: "Home",
      startDate: tomorrow.toDate(),
      disableAutoAssign: false,
      repeatEveryUnit: "none",
      repeatAmount: null,
    } as InspectorServiceTravelRuleResponse;

    if (ruleToCopy) {
      newRule.name = ruleToCopy.name;
      copyTravelRuleRegions(ruleToCopy, newRuleId);
    }
    
    setEditingRule(newRule, !ruleToCopy);
    setEditingRuleExtraData(null);
  };

  const onStartEdit = (ruleToEdit: InspectorServiceTravelRuleResponse) => {
    if (moment(ruleToEdit.startDate).startOf("day").isBefore(tomorrow)) {
      setEditingRuleExtraData({ dateToEdit: tomorrow.toDate(), editType: CalendarUpdateEnum.SingleDayAndFuture });
      ruleToEdit = { ...ruleToEdit };
      ruleToEdit.startDate = tomorrow.toDate();
    }

    setEditingRule(ruleToEdit);
  };

  const onCancelEdit = () => {
    setEditingRuleExtraData(null);
    setEditingRule(null);
  };

  // we set the id to -1 when editing new rules
  const isEditingNewRule = () => editingRule?.id === newRuleId;

  const onSaveTravelRule = (updatedTravelRule: InspectorServiceTravelRuleResponse) => {
    let baseRequest: InspectorServiceTravelRuleAddRequest = {
      inspectorNumber: updatedTravelRule.inspectorNumber,
      ruleName: updatedTravelRule.name,
      startingLocationType: updatedTravelRule.startingLocationType,
      disableAutoAssign: updatedTravelRule.disableAutoAssign,
      startDate: updatedTravelRule.startDate,
      endDate: updatedTravelRule.endDate,
      recursOn: updatedTravelRule.recursOn,
      serviceRegions: selectedServiceRegions.map(r => ({ countyId: r.countyId, serviceRegionId: r.serviceRegionId, postalId: r.postalId })),
      address: updatedTravelRule.address,
      city: updatedTravelRule.city,
      state: updatedTravelRule.state,
      postalCode: updatedTravelRule.postalCode,
      repeatAmount: updatedTravelRule.repeatAmount,
      repeatEveryUnit: updatedTravelRule.repeatEveryUnit,
    }

    if (selectedServiceRegions.length === 0) {
      dialogContext.showOk("No Regions Selected", "You must select at least one region to save a secondary event.");
      return;
    }

    const errorApiCallback = async (param: Response) => {
      apiCallback();
      errorToast(param);
    };

    const apiCallback = async () => {
      setEditingRuleExtraData(null);
      setEditingRule(null);
      setIsSavingRule(false);
      reloadTravelRulesAndRegions?.();
    };

    setIsSavingRule(true);
    if (isEditingNewRule()) {
      addInspectorTravelRule(baseRequest, apiCallback, errorApiCallback, mounted);
    } else {
      let request: InspectorServiceTravelRuleUpdateRequest = {
        ...baseRequest,
        travelRuleId: updatedTravelRule.id,
        updateType: editingRuleExtraData?.editType ?? CalendarUpdateEnum.EntireEvent,
        dateToUpdate: editingRuleExtraData?.dateToEdit,
      };

      updateInspectorTravelRule(request, apiCallback, errorApiCallback, mounted);
    }
  };
  
  let paneTitle: ReactNode;

  if (isEditingMSAs) {
    let paneButtons = [
      { icon: "close", onClick: () => changeIsEditingMSAs(false) },
      { icon: "check", onClick: () => saveEditedMSAList() },
    ];

    paneTitle = (
      <div className="region-editor-title editing-msas">
        <span>Editing MSAs</span>
        {paneButtons.map(b => <IconButton { ...b } />)}
      </div>
    );
  } else {
    let editMsaButton = <Button raised primary onClick={() => changeIsEditingMSAs(true)}>Edit MSAs</Button>;
    if (!availableMSAList || !selectedServiceRegions || isSavingRegions) editMsaButton = null;

    paneTitle = <div className="region-editor-title">Area List {editMsaButton}</div>;
  }
  
  let control: ReactNode;

  if (editingRule) {
    let title = editingRule.id ? "Editing Secondary Event" : "Create New Secondary Event";

    control = (
      <>
        <div className="region-editor-title">{title}</div>
        {isSavingRule && <LoadingCover loadingMessages={["Saving Secondary Event..."]} />}
        <TravelRuleEntryForm
          // @ts-ignore go home typescript, youre drunk... it doesnt think the rule types line up here but i promise they do
          travelRule={editingRule}
          onSaveClicked={onSaveTravelRule}
          onCancelClicked={onCancelEdit}
          stateList={stateList}
        >
          <RegionManagementPane
            onDeleteRef={onDeleteRef}
            selectedServiceRegions={selectedServiceRegions?.filter(s => s.travelRuleId === editingRule.id)}
            isLoading={isLoading}
            isEditingMSAs={isEditingMSAs}
            availableMSAList={availableMSAList}
            selectedMSAs={msaEditorSelectedIndicies}
            onSelectedMSAsChanged={setMSAEditorSelectedIndicies}
          />
        </TravelRuleEntryForm>
      </>
    );
  } else {
    let ruleList = (
      <div className="travel-rule-list-parent">
        <div className="region-editor-title travel-rule-list">
          <div className="title-line-1">
            <span>Secondary Events</span><Button raised primary onClick={() => blockActionIfPrimaryChangesPresent(createNewRule)}>Add Event</Button>
          </div>
          <MSelect
            className="travel-rule-date-filter"
            options={travelRuleFilterOptions}
            value={travelFilter}
            onChange={option => setTravelFilter(option)}
            isClearable={false}
          />
        </div>
        <div className="rule-list-pane">
          {travelRules?.map(r => (
            <TravelRuleCard
              travelRule={r}
              onEditClicked={rule => blockActionIfPrimaryChangesPresent(() => onStartEdit(rule))}
              onDeleteClicked={onDeleteRule}
              onCopyClicked={rule => blockActionIfPrimaryChangesPresent(() => createNewRule(rule))}
              onMouseEvent={event => event === "enter" ? onTravelRuleHover(r) : onTravelRuleHover(null)}
            />)) ?? "Loading Secondary Events..."}
        </div>
      </div>
    );
    
    let regionManagementPane = <>
      {paneTitle}
      <RegionManagementPane
        onDeleteRef={onDeleteRef}
        selectedServiceRegions={selectedServiceRegions?.filter(r => !r.travelRuleId)}
        isLoading={isLoading}
        isEditingMSAs={isEditingMSAs}
        availableMSAList={availableMSAList}
        selectedMSAs={msaEditorSelectedIndicies}
        onSelectedMSAsChanged={setMSAEditorSelectedIndicies}
      />
    </>;

    let sections = [
      {
        id: "primaryTab" as RegionManagementTabId,
        title: "Primary",
        children: regionManagementPane,
      },
      {
        id: "secondaryTab" as RegionManagementTabId,
        title: "Secondary",
        children: ruleList,
      },
    ];

    if (isEditingMSAs) {
      control = regionManagementPane;
    } else {
      control = (
        <MSectionCollection
          sections={sections}
          defaultMode={MSECTION_MODE_TAB}
          tabModeCollapsible={false}
          disableModeChanging
          fullHeight
          initialTab={currentTabId}
          onTabChange={(tabIndex: number) => setCurrentTabId(sections[tabIndex].id)}
        />
      );
    }
  }

  return (
    <div className="region-management-tabs">
      {control}
    </div>
  );
};

const mapStateToProps = state => ({
  stateList: state.appraiser.stateList, // dont blame me that someone thought we should put states here, these are loaded on site load tho, no point in getting them again
});

const mapDispatchToProps = (dispatch) => ({
  getStateList() {
    dispatch(getStateList());
  },
  errorToast(response: Response) {
    responseHandler.error(response, dispatch, false);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(RegionManagementTabs);
